import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DebounceClickService {
  private clickStates = new Map<string, BehaviorSubject<boolean>>();
  private timeouts = new Map<string, any>();

  /**
   * Verifica si una acción está en progreso
   */
  isInProgress(actionKey: string): Observable<boolean> {
    if (!this.clickStates.has(actionKey)) {
      this.clickStates.set(actionKey, new BehaviorSubject<boolean>(false));
    }
    return this.clickStates.get(actionKey)!.asObservable();
  }

  /**
   * Marca una acción como en progreso y la desbloquea después del tiempo especificado
   */
  setInProgress(actionKey: string, duration: number = 2000): boolean {
    const state = this.clickStates.get(actionKey);
    
    // Si ya está en progreso, bloquear
    if (state && state.value) {
      console.log(`🚫 DebounceClick: Acción '${actionKey}' bloqueada - ya en progreso`);
      return false;
    }

    // Marcar como en progreso
    if (!state) {
      this.clickStates.set(actionKey, new BehaviorSubject<boolean>(true));
    } else {
      state.next(true);
    }

    // Limpiar timeout anterior si existe
    if (this.timeouts.has(actionKey)) {
      clearTimeout(this.timeouts.get(actionKey));
    }

    // Programar desbloqueo
    const timeout = setTimeout(() => {
      this.clearInProgress(actionKey);
    }, duration);
    
    this.timeouts.set(actionKey, timeout);
    
    console.log(`✅ DebounceClick: Acción '${actionKey}' permitida - bloqueada por ${duration}ms`);
    return true;
  }

  /**
   * Limpia el estado de progreso de una acción
   */
  clearInProgress(actionKey: string): void {
    const state = this.clickStates.get(actionKey);
    if (state) {
      state.next(false);
    }
    
    if (this.timeouts.has(actionKey)) {
      clearTimeout(this.timeouts.get(actionKey));
      this.timeouts.delete(actionKey);
    }
    
    console.log(`🔓 DebounceClick: Acción '${actionKey}' desbloqueada`);
  }

  /**
   * Verifica si una acción puede ejecutarse (no está en progreso)
   */
  canExecute(actionKey: string): boolean {
    const state = this.clickStates.get(actionKey);
    return !state || !state.value;
  }

  /**
   * Ejecuta una acción solo si no está en progreso
   */
  executeIfAllowed<T>(actionKey: string, action: () => T, duration: number = 2000): T | null {
    if (this.setInProgress(actionKey, duration)) {
      return action();
    }
    return null;
  }
}